import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import NotificationsDropdown from 'components/notifications-dropdown/NotificationsDropdown';
import NotificationBadge from 'components/notification-badge/NotificationBadge';
import type { NotificationsDropdownItem } from 'components/notifications-dropdown/NotificationsDropdown';

import { __t, applyTranslations } from 'utils/language';
import { isBrandsFocusedOrganization } from 'utils/organization';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import './header-notifications.scss';

applyTranslations(require('./header-notifications.i18n.json'));

import type { UnreadMessages } from 'types';

type OwnProps = {};

type StateProps = {
  unreadMessages: UnreadMessages,
  isBrand: boolean
};

type Props = OwnProps & StateProps;

type State = {
  isDropdownVisible: boolean
};

class HeaderNotifications extends React.PureComponent<Props, State> {
  state = {
    isDropdownVisible: false
  };

  handleToggleNotifications = () => {
    const { isDropdownVisible } = this.state;

    this.setState({
      isDropdownVisible: !isDropdownVisible
    });

    if (!isDropdownVisible) {
      addUserAction({
        eventCategory: 'header-notifications',
        eventAction: 'dropdown-opened'
      });
    }
  };

  handleNotificationSelected = () => {
    this.setState({
      isDropdownVisible: false
    });

    addUserAction({
      eventCategory: 'header-notifications',
      eventAction: 'notification-clicked'
    });
  };

  getUnreadItems(translationKey: string): Array<NotificationsDropdownItem> {
    const { unreadMessages } = this.props;

    return unreadMessages.conversations
      .map(chat => ({
        title: chat.campaignName,
        subtitle: chat.unreadConversationsCount === 1
          ? __t(`header_notifications.${translationKey}_label`)
          : __t(`header_notifications.${translationKey}s_label`, {
            count: chat.unreadConversationsCount
          }),
        datetime: chat.lastUpdated ?? '',
        id: String(chat.campaignId),
        new: true,
        campaignId: chat.campaignId,
        campaignType: chat.campaignType,
        subcampaignId: chat.subcampaignId,
        externalConversationId: (chat.unreadConversationsExternalIds?.length ?? 0) === 1
          ? (chat?.unreadConversationsExternalIds ?? [])[0] ?? undefined
          : undefined
      }))
      .sort((a, b) => b.datetime.localeCompare(a.datetime));
  }

  render(): React.ReactNode {
    const { unreadMessages, isBrand } = this.props;
    const { isDropdownVisible } = this.state;

    if (isEmpty(unreadMessages?.conversations)) {
      return null;
    }

    const messageKey = isBrand ? 'new_conversation' : 'new_message';
    const items = this.getUnreadItems(messageKey);
    const unreadConversationsCount = unreadMessages.totalUnreadConversations;

    return (
      <React.Fragment>
        <div className="header-notifications">
          <div
            className="header-notifications__menu-item"
            onClick={this.handleToggleNotifications}
          >
            <div className="header-notifications__icon">
              <SVGDynamicIcon
                name="bell-custom-fg"
                width={21}
                height={25}
                customColor="white"
              />
              <div className="header-notifications__badge">
                <NotificationBadge
                  value={unreadConversationsCount}
                  size="normal"
                />
              </div>
            </div>
          </div>
          {
            isDropdownVisible && (
              <div className="header-notifications__dropdown">
                <NotificationsDropdown
                  items={items}
                  isBrand={isBrand}
                  onClick={this.handleNotificationSelected}
                />
              </div>
            )
          }
        </div>
        {
          isDropdownVisible && (
            <div
              onClick={this.handleToggleNotifications}
              className="header-notifications__overlay"
            />
          )
        }
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ unreadMessages, organizations }): StateProps => ({
  unreadMessages,
  isBrand: isBrandsFocusedOrganization(organizations.currentOrganization)
});

export default connect(mapStateToProps)(HeaderNotifications);
